import React from 'react';
import { Typography, Box, Grid, Card, Stack, Divider, Button as MuiButton, Chip, CircularProgress } from '@mui/material';
import { Download, CreditCard, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

export default function Billing() {
    const { subscription, billingHistory, isLoading } = useAppContext();

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const activePlan = subscription?.plan || 'Free';
    const planStatus = subscription?.status || 'Active';
    const nextRenewal = subscription?.currentPeriodEnd
        ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : 'Continuous';

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={800} sx={{ color: '#111827', mb: 1, letterSpacing: '-0.02em' }}>
                    Billing & Subscription
                </Typography>
                <Typography variant="body1" color="#64748b">
                    Manage your subscription, payment methods, and view your billing history.
                </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Active Plan Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="#94a3b8" letterSpacing="0.05em">
                                ACTIVE PLAN
                            </Typography>
                            <Chip
                                label={planStatus.toUpperCase()}
                                size="small"
                                sx={{
                                    bgcolor: planStatus === 'Active' ? '#dcfce7' : '#fee2e2',
                                    color: planStatus === 'Active' ? '#166534' : '#991b1b',
                                    fontWeight: 800,
                                    fontSize: '0.65rem',
                                    letterSpacing: '0.05em',
                                    borderRadius: 1,
                                    height: 24
                                }}
                            />
                        </Box>

                        <Typography variant="h6" fontWeight={700} color="#111827" mb={1}>
                            {activePlan} Plan
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
                            <Typography variant="h4" fontWeight={800} color="#4f46e5" sx={{ letterSpacing: '-0.02em' }}>
                                {activePlan === 'Pro' ? '$99' : activePlan === 'Enterprise' ? '$299' : '$0'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="#94a3b8">
                                /month
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 4, flexGrow: 1 }}>
                            <Typography variant="caption" color="#64748b" fontWeight={600} display="block" gutterBottom>
                                Next renewal date
                            </Typography>
                            <Typography variant="body2" color="#111827" fontWeight={600}>
                                {nextRenewal}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={2}>
                            <MuiButton
                                fullWidth
                                variant="contained"
                                onClick={() => alert('Subscription management will be available once Stripe is connected.')}
                                sx={{
                                    bgcolor: '#f1f5f9',
                                    color: '#0f172a',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: '#e2e8f0', boxShadow: 'none' }
                                }}
                            >
                                Manage Plan
                            </MuiButton>
                        </Stack>
                    </Card>
                </Grid>

                {/* Payment Method Card */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" fontWeight={700} color="#94a3b8" letterSpacing="0.05em" mb={3} display="block">
                            PAYMENT METHOD
                        </Typography>

                        {activePlan === 'Free' || !subscription?.paymentMethod ? (
                            // No payment method — show connect prompt
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 2 }}>
                                <Box sx={{ width: 56, height: 56, bgcolor: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                    <CreditCard size={24} color="#94a3b8" />
                                </Box>
                                <Typography variant="body2" fontWeight={700} color="#111827" mb={0.5}>
                                    No payment method added
                                </Typography>
                                <Typography variant="caption" color="#64748b" sx={{ mb: 3, lineHeight: 1.6 }}>
                                    Add a payment method to upgrade to a paid plan and unlock unlimited generations.
                                </Typography>
                                <MuiButton
                                    fullWidth
                                    variant="contained"
                                    onClick={() => alert('Stripe integration coming soon — payment setup will be available here.')}
                                    startIcon={<CreditCard size={16} />}
                                    sx={{
                                        bgcolor: '#4f46e5',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        boxShadow: 'none',
                                        '&:hover': { bgcolor: '#4338ca', boxShadow: 'none' }
                                    }}
                                >
                                    Connect Payment Method
                                </MuiButton>
                            </Box>
                        ) : (
                            // Has payment method
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 3, border: '1px solid #f1f5f9', bgcolor: '#f8fafc', mb: 4 }}>
                                    <Box sx={{ width: 48, height: 32, bgcolor: '#1e293b', borderRadius: 1, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CreditCard color="white" size={20} />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body2" fontWeight={700} color="#111827">
                                            {subscription.paymentMethod}
                                        </Typography>
                                        <Typography variant="caption" color="#64748b">
                                            Renews on {nextRenewal}
                                        </Typography>
                                    </Box>
                                    <CheckCircle2 size={18} color="#10b981" />
                                </Box>
                                <Typography variant="body2" color="#64748b" sx={{ mb: 4, flexGrow: 1, lineHeight: 1.6 }}>
                                    Your next payment will be processed automatically on {nextRenewal}.
                                </Typography>
                                <MuiButton
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => alert('Stripe integration coming soon.')}
                                    sx={{
                                        color: '#111827',
                                        borderColor: '#e2e8f0',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                                    }}
                                >
                                    Update Payment Method
                                </MuiButton>
                            </>
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* Billing History */}
            <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700} color="#111827">
                        Billing History
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                        All invoices are in USD
                    </Typography>
                </Box>

                <Divider />

                {billingHistory.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                <th style={{ padding: '16px 32px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Invoice Date</th>
                                <th style={{ padding: '16px 32px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                                <th style={{ padding: '16px 32px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                                <th style={{ padding: '16px 32px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingHistory.map((invoice: any) => (
                                <tr key={invoice.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '16px 32px' }}>
                                        <Typography variant="body2" fontWeight={600} color="#334155">
                                            {new Date(invoice.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </Typography>
                                    </td>
                                    <td style={{ padding: '16px 32px' }}>
                                        <Typography variant="body2" fontWeight={700} color="#111827">
                                            ${invoice.amount.toFixed(2)}
                                        </Typography>
                                    </td>
                                    <td style={{ padding: '16px 32px' }}>
                                        <Chip
                                            label={invoice.status.toUpperCase()}
                                            size="small"
                                            sx={{
                                                bgcolor: invoice.status === 'Paid' ? '#dcfce7' : '#fee2e2',
                                                color: invoice.status === 'Paid' ? '#166534' : '#991b1b',
                                                fontWeight: 800,
                                                fontSize: '0.65rem',
                                                borderRadius: 1,
                                                height: 20
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: '16px 32px', textAlign: 'right' }}>
                                        <MuiButton
                                            startIcon={<Download size={14} />}
                                            sx={{
                                                color: '#4f46e5',
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                textTransform: 'none',
                                                p: 0,
                                                '&:hover': { background: 'none', textDecoration: 'underline' }
                                            }}
                                        >
                                            Download
                                        </MuiButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <Box sx={{ p: 8, textAlign: 'center' }}>
                        <Typography variant="body1" color="#64748b">No invoices found yet.</Typography>
                    </Box>
                )}
            </Card>
        </Box>
    );
}
